/* eslint-disable */
import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import PublicSeoPage from '../../components/shared/PublicSeoPage';
import PageMeta from '../../components/shared/PageMeta';
import { DOWNLOAD_PATHS } from '../../config/downloads';

// The page the agent has been sending iPhone users to.
//
// core/install_links.py in HARTOS maps ('ios', 'default') to
// https://hevolve.ai/ios-coming-soon, and any visitor who asks Nunba to send
// them the app on an iPhone gets that link. It was never a route. The host
// answers an unknown path with build/index.html, so every one of those people
// landed on the homepage with no explanation of why, and no answer to the
// question they had actually asked.
//
// So this says the true thing plainly: there is no iOS build yet. Then it does
// the only useful thing left, which is offer the platforms that do exist. It
// deliberately does not collect an email for a launch list, because there is no
// list and pretending otherwise to somebody who just wanted an app is worse
// than telling them no.
//
// Static, so it prerenders cleanly. No em dashes in any rendered string.

const ALTERNATIVES = [
  {
    name: 'Mac, Windows or Linux',
    body: 'The desktop app is the full product: voice, vision, chat, and the model running on your own machine. If you have a laptop, this is the one to get.',
    to: '/download',
    cta: 'Get the desktop app',
    color: '#17c07f',
  },
  {
    name: 'Android or Wear OS',
    body: 'Both ship today from the same Play Store listing, and the watch build pairs with the phone. If you have an Android device in the house, it works now.',
    href: DOWNLOAD_PATHS.android,
    cta: 'Open Google Play',
    color: '#34d3ff',
  },
  {
    name: 'Right here in the browser',
    body: 'The agents run on the web too. Nothing to install, and it works on an iPhone today. Less private than local, and we would rather say so than not.',
    to: '/agents',
    cta: 'Use it in the browser',
    color: '#7c72ff',
  },
];

export default function IosComingSoonPage() {
  return (
    <>
      <PageMeta
        title="Nunba on iPhone: Not Yet, and What Works Today | Hevolve"
        description="There is no iOS build of Nunba yet. The desktop app for Mac, Windows and Linux is the full product, Android and Wear OS ship today, and the agents run in the browser on an iPhone right now."
        ogTitle="Nunba on iPhone: not yet"
        ogDescription="No iOS build yet. Here is what actually works today, including in the browser on your phone."
        path="/ios-coming-soon"
      >
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Nunba on iPhone',
          url: 'https://hevolve.ai/ios-coming-soon',
          description: 'There is no iOS build of Nunba yet. The desktop app, Android, Wear OS and the browser all work today.',
          publisher: { '@type': 'Organization', name: 'Hevolve AI', url: 'https://hevolve.ai' },
        })}</script>
      </PageMeta>

      <PublicSeoPage
        heading="There is no iPhone app yet"
        subheading="You asked for Nunba on iOS and we do not have it. Rather than leave you on a page that does not say so, here is exactly where things stand and what works today."
        maxWidth="md"
      >
        <Box sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(240,178,58,0.07)', border: '1px solid rgba(240,178,58,0.24)', mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>Where it actually is</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.76)', lineHeight: 1.75 }}>
            Nunba runs a language model on the device it is installed on. That is the whole point of it, and it is
            also the reason iOS is the hardest of the platforms rather than the easiest. There is no TestFlight
            build to hand you today. When there is one, it will be linked from the download page along with
            everything else, and this page will say so instead of what it says now.
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 800, color: '#fff', mb: 0.5 }}>What works today</Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2.5 }}>
          Including one option you can use on the phone in your hand right now.
        </Typography>

        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          {ALTERNATIVES.map((a) => (
            <Grid item xs={12} md={4} key={a.name}>
              <Box sx={{ height: '100%', p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column', gap: 1.25,
                background: 'rgba(255,255,255,0.03)', border: `1px solid ${a.color}44`,
                transition: 'transform .16s, border-color .2s',
                '&:hover': { transform: 'translateY(-3px)', borderColor: a.color } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>{a.name}</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.74)', lineHeight: 1.7 }}>{a.body}</Typography>
                {a.href ? (
                  <Typography component="a" href={a.href} target="_blank" rel="noopener noreferrer"
                    sx={{ mt: 'auto', fontWeight: 700, fontSize: '0.9rem', color: a.color, textDecoration: 'none', '&:hover': { color: '#fff' } }}>
                    {a.cta} &rarr;
                  </Typography>
                ) : (
                  <Typography component={Link} to={a.to}
                    sx={{ mt: 'auto', fontWeight: 700, fontSize: '0.9rem', color: a.color, textDecoration: 'none', '&:hover': { color: '#fff' } }}>
                    {a.cta} &rarr;
                  </Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(124,114,255,0.06)', border: '1px solid rgba(124,114,255,0.2)' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>If you want to know when it lands</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.74)', lineHeight: 1.7, mb: 2 }}>
            There is no mailing list for this and we are not going to make one just to collect your address. Watch
            the releases on GitHub instead. It is the same place the build would appear, and it tells you the day it
            does rather than whenever a newsletter goes out.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button component="a" href="https://github.com/hertz-ai/Nunba/releases" target="_blank" rel="noopener noreferrer"
              variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#7c72ff' } }}>
              Watch the releases
            </Button>
            <Button component={Link} to="/download" variant="outlined"
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#7c72ff' } }}>
              See every platform
            </Button>
          </Stack>
        </Box>
      </PublicSeoPage>
    </>
  );
}
